import { Route } from 'vue-router';
import Vue from 'vue';
import { NavigationGuardNext, NavigationGuard } from 'vue-router/types/router';
import Interceptor from '../servicelayer/interceptor';
import _ from '../../utils';

type NavigationGuardRoute = Parameters<NavigationGuardNext<Vue>>['0'] | Route;
type GuardReturnType = string | Promise<string> | void | Promise<void>;

export type Guard = {
  /** Name the guard so it is easier to identify for debugging */
  name: string;

  /** Disables a guard, no hooks are called when it's disabled */
  disabled?: boolean;

  /**
   * Called during app intialization,
   * the app will wait for every appLoad promise to finish before displaying anything.
   * appLoads are synchronous, each appLoad is executed in the order they are added.
   */
  appLoad?: () => Promise<void>;

  registerInterceptor?: () => Interceptor;
  /**
   * Before guards are called in creation order, whenever a navigation is triggered
   * [See the docs for more info](https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards)
   */
  routeBeforeEnter?: (to: NavigationGuardRoute, from: NavigationGuardRoute | undefined) => GuardReturnType;
  /**
   * This is called after the component for the route has been loaded.
   * Resolve guards will be called right before the navigation is confirmed,
   * after all in-component guards and async route components are resolved.
   * [See the docs for more info](https://router.vuejs.org/guide/advanced/navigation-guards.html#global-resolve-guards)
   */
  routeResolved?: (to: NavigationGuardRoute, from: NavigationGuardRoute | undefined) => GuardReturnType;
  /**
   * Registers a global after hook, however unlike guards,
   * this hook does not get a next function and cannot affect the navigation.
   * [See the docs for more info](https://router.vuejs.org/guide/advanced/navigation-guards.html#global-after-hooks)
   */
  routeAfterLeave?: (to: NavigationGuardRoute, from: NavigationGuardRoute) => void | Promise<void>;
};

type NavigationGuardHooks = NonNullable<Guard['routeBeforeEnter']> | NonNullable<Guard['routeResolved']> | NonNullable<Guard['routeAfterLeave']>;

async function navGuardWrapperFunc(to: NavigationGuardRoute, from: NavigationGuardRoute | undefined, next: NavigationGuardNext | undefined, wrappedFunc: NavigationGuardHooks | undefined, guard: Guard) {
  if (wrappedFunc && !guard.disabled) {
    let returned = wrappedFunc(to, from);
    if (_.isPromise(returned)) {
      const timeout = 5000;
      const handle = setTimeout(() => {
        Vue.$log.warning(`🛡️ [GUARD] Nav-Guard ${guard.name} took longer than ${timeout / 1000}s to complete. Guards should not take this long to complete.`);
      }, timeout);
      returned = await returned;
      clearTimeout(handle);
    }
    Vue.$log.info(`🛡️ [GUARD] Nav-Guard ${guard.name} finished checking`);
    if (typeof next === 'function') {
      if (returned) {
        Vue.$log.info(`🛡️ [GUARD] Nav-Guard ${guard.name} is redirecting: ${JSON.stringify(returned)}`);
        next(returned);
      }
    }
  }
  if (typeof next === 'function') {
    next();
  }
}

export default class GuardWrapper {
  guards: Guard[] = [];
  appLoadPromises: Promise<void>[] = [];
  private previousAppLoads: Promise<void>[] = [];

  waitForAppLoad() {
    return new Promise<void>((resolve) => {
      // Wait a tick for all guards to be registered
      // Wait so guards registered after core has been initalised are also waited upon
      setTimeout(() => {
        Promise.all(this.appLoadPromises)
          .then(() => resolve());
      }, 1);
    });
  }

  async push(guard: Guard) {
    this.guards.push(guard);
    if (!guard.disabled) {
      if (typeof guard.appLoad === 'function') {
        Vue.$log.debug(`🛡️ [GUARD] ${guard.name} registered appLoad...`);
        // eslint-disable-next-line no-async-promise-executor
        this.appLoadPromises.push(new Promise(async (resolve) => {
          // Even if it's an empty array it skips a tick if we await it
          // The first entry should never be delayed (even by a tick)
          if (this.previousAppLoads.length > 0) {
            await Promise.all(this.previousAppLoads);
          }
          const appLoadPromise = guard.appLoad!();
          this.previousAppLoads.push(appLoadPromise);
          await appLoadPromise;
          resolve();
        }));
      }
      const nextFunction = (to?: Parameters<NavigationGuardNext>[0]) => { if (to) { Vue.$router.push(to as any); } };
      if (typeof guard.registerInterceptor === 'function') {
        // TODO: This will not be activated if the guard switches from disabled to enabled
        const interceptor = guard.registerInterceptor();
        Vue.$service.interceptors.push(interceptor);
      }
      if (typeof guard.routeResolved === 'function') {
        const wrappedBeforeResolve = (to: Route, from: Route | undefined, next: NavigationGuardNext) => navGuardWrapperFunc(to, from, next, guard.routeResolved, guard);
        Vue.$router.onReady(() => {
          // The Vue Router does not call the guards on initial page load only on navigation
          // So a manual call is needed
          wrappedBeforeResolve(Vue.$router.currentRoute, undefined, nextFunction);
        });
        Vue.$router.beforeResolve(wrappedBeforeResolve);
      }
      if (typeof guard.routeBeforeEnter === 'function') {
        const wrappedBeforeEnter = (to: Route, from: Route | undefined, next: NavigationGuardNext) => navGuardWrapperFunc(to, from, next, guard.routeBeforeEnter, guard);
        Vue.$router.onReady(() => {
          // The Vue Router does not call the guards on initial page load only on navigation
          // So a manual call is needed
          wrappedBeforeEnter(Vue.$router.currentRoute, undefined, nextFunction);
        });
        Vue.$router.beforeEach(wrappedBeforeEnter);
      }
      if (typeof guard.routeAfterLeave === 'function') {
        const wrappedAfterLeave = (to: Route, from: Route) => navGuardWrapperFunc(to, from, undefined, guard.routeAfterLeave, guard);
        Vue.$router.afterEach(wrappedAfterLeave);
      }
    }
  }
}
